import { forwardRef, useId } from 'react';
import { cn } from '../../lib/format.js';

/**
 * Form controls.
 *
 * Every one is labelled, every error is announced, and the error message sits
 * under the field rather than in a toast — PRD §8: errors state what happened
 * and what to do next, and a shopper needs to read that while looking at the
 * field they got wrong.
 */

const controlBase =
  'w-full rounded border bg-surface px-3.5 text-sm text-ink placeholder:text-muted/70 ' +
  'transition-colors duration-150 focus:outline-2 focus:outline-offset-1 ' +
  'disabled:cursor-not-allowed disabled:bg-paper disabled:text-muted';

const controlState = (invalid) =>
  invalid
    ? 'border-danger focus:outline-danger'
    : 'border-mist hover:border-muted/60 focus:outline-accent';

export const Label = ({ htmlFor, required, hint, className, children }) => (
  <div className={cn('mb-1.5 flex items-baseline justify-between gap-3', className)}>
    <label htmlFor={htmlFor} className="text-xs font-medium text-ink">
      {children}
      {required && (
        <span className="ml-1 text-muted" aria-hidden="true">
          *
        </span>
      )}
    </label>
    {hint && <span className="text-2xs text-muted">{hint}</span>}
  </div>
);

export const FieldError = ({ id, children }) =>
  children ? (
    <p id={id} role="alert" className="mt-1.5 flex items-start gap-1.5 text-2xs text-danger">
      <svg viewBox="0 0 16 16" className="mt-px size-3.5 shrink-0" fill="currentColor" aria-hidden="true">
        <path d="M8 1.5 15 14H1L8 1.5Zm0 4.25a.75.75 0 0 0-.75.75v3a.75.75 0 0 0 1.5 0v-3A.75.75 0 0 0 8 5.75Zm0 6.75a.9.9 0 1 0 0-1.8.9.9 0 0 0 0 1.8Z" />
      </svg>
      {children}
    </p>
  ) : null;

export const Input = forwardRef(function Input(
  { label, error, hint, help, required, className, id: providedId, ...props },
  ref,
) {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  const errorId = `${id}-error`;
  const helpId = `${id}-help`;

  return (
    <div className={className}>
      {label && (
        <Label htmlFor={id} required={required} hint={hint}>
          {label}
        </Label>
      )}
      <input
        ref={ref}
        id={id}
        required={required}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={cn(error && errorId, help && helpId) || undefined}
        className={cn(controlBase, controlState(error), 'h-11')}
        {...props}
      />
      {help && !error && (
        <p id={helpId} className="mt-1.5 text-2xs text-muted">
          {help}
        </p>
      )}
      <FieldError id={errorId}>{error}</FieldError>
    </div>
  );
});

export const Textarea = forwardRef(function Textarea(
  { label, error, hint, help, required, rows = 4, className, id: providedId, ...props },
  ref,
) {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  const errorId = `${id}-error`;

  return (
    <div className={className}>
      {label && (
        <Label htmlFor={id} required={required} hint={hint}>
          {label}
        </Label>
      )}
      <textarea
        ref={ref}
        id={id}
        rows={rows}
        required={required}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? errorId : undefined}
        className={cn(controlBase, controlState(error), 'resize-y py-3 leading-relaxed')}
        {...props}
      />
      {help && !error && <p className="mt-1.5 text-2xs text-muted">{help}</p>}
      <FieldError id={errorId}>{error}</FieldError>
    </div>
  );
});

export const Select = forwardRef(function Select(
  { label, error, hint, required, options = [], placeholder, className, id: providedId, children, ...props },
  ref,
) {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  const errorId = `${id}-error`;

  return (
    <div className={className}>
      {label && (
        <Label htmlFor={id} required={required} hint={hint}>
          {label}
        </Label>
      )}
      <div className="relative">
        <select
          ref={ref}
          id={id}
          required={required}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? errorId : undefined}
          className={cn(controlBase, controlState(error), 'h-11 appearance-none pr-9')}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
          {children}
        </select>
        <svg
          viewBox="0 0 16 16"
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 right-3 size-3.5 -translate-y-1/2 text-muted"
          fill="currentColor"
        >
          <path d="M4.2 6.2a.75.75 0 0 1 1.06 0L8 8.94l2.74-2.74a.75.75 0 1 1 1.06 1.06L8.53 10.53a.75.75 0 0 1-1.06 0L4.2 7.26a.75.75 0 0 1 0-1.06Z" />
        </svg>
      </div>
      <FieldError id={errorId}>{error}</FieldError>
    </div>
  );
});

export const Checkbox = forwardRef(function Checkbox(
  { label, description, error, className, id: providedId, ...props },
  ref,
) {
  const generatedId = useId();
  const id = providedId ?? generatedId;

  return (
    <div className={className}>
      <div className="flex items-start gap-2.5">
        <input
          ref={ref}
          id={id}
          type="checkbox"
          className={cn(
            'mt-0.5 size-4 shrink-0 cursor-pointer appearance-none rounded-[3px] border border-mist bg-surface',
            'checked:border-ink checked:bg-ink',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
            'bg-[length:11px] bg-center bg-no-repeat',
            "checked:bg-[url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23fff'%3E%3Cpath d='M6.2 11.5 3 8.3l1.06-1.06L6.2 9.4l5.74-5.74L13 4.72z'/%3E%3C/svg%3E\")]",
            error && 'border-danger',
          )}
          {...props}
        />
        <div className="min-w-0">
          <label htmlFor={id} className="cursor-pointer text-xs text-ink">
            {label}
          </label>
          {description && <p className="mt-0.5 text-2xs text-muted">{description}</p>}
        </div>
      </div>
      <FieldError>{error}</FieldError>
    </div>
  );
});

export const Radio = forwardRef(function Radio({ label, description, className, id: providedId, ...props }, ref) {
  const generatedId = useId();
  const id = providedId ?? generatedId;

  return (
    <div className={cn('flex items-start gap-2.5', className)}>
      <input
        ref={ref}
        id={id}
        type="radio"
        className={cn(
          'mt-0.5 size-4 shrink-0 cursor-pointer appearance-none rounded-full border border-mist bg-surface',
          'checked:border-[5px] checked:border-ink',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
        )}
        {...props}
      />
      <div className="min-w-0">
        <label htmlFor={id} className="cursor-pointer text-xs text-ink">
          {label}
        </label>
        {description && <p className="mt-0.5 text-2xs text-muted">{description}</p>}
      </div>
    </div>
  );
});

/** A whole card as the radio target — used for address and payment choices. */
export const RadioCard = ({ checked, onChange, name, value, title, children, className }) => (
  <label
    className={cn(
      'flex cursor-pointer gap-3 border p-4 transition-colors',
      checked ? 'border-ink bg-paper' : 'border-mist hover:border-muted/60',
      className,
    )}
  >
    <input
      type="radio"
      name={name}
      value={value}
      checked={checked}
      onChange={onChange}
      className="mt-0.5 size-4 shrink-0 cursor-pointer appearance-none rounded-full border border-mist bg-surface checked:border-[5px] checked:border-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
    />
    <div className="min-w-0 flex-1">
      {title && <p className="text-xs font-medium text-ink">{title}</p>}
      {children}
    </div>
  </label>
);
